import { Model } from 'app/model/backendmodel';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { AdapterContext } from 'shell-models-lib';
import { AzureKeyVaultLinkedService } from 'app/model/linkedservice/azure-key-vault.linkedservice';
import { IAttribute } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Constants } from 'app/shared/shared-constants';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { ValueAdapter } from '../value.adapter';
import { fillParameterValuesForResource, convertParametersToModel } from 'app/shared/utils/parameter-converter.util';
import { getCachedServiceInstanceSync } from 'app/multi-contexts/modules/root/root.application';
import { LoggerService } from 'app/logger/logger.service';

export function passwordOrAKVtoSecretBase(secret: string | AzureKeyVault | undefined, context: AdapterContext): Model.SecretBase {
    const secretBase: Model.SecretBase | undefined = secret instanceof AzureKeyVault ? akvToResource(secret, context) : (
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        !context.removeEncryptedCredentials && stringIsNullOrEmpty(secret) ? undefined : {
            type: 'SecureString',
            value: !!secret ? secret : Constants.maskedCredential
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        } as Model.SecureString
    );

    return secretBase;
}
/**
 * @param secretBase Secret value
 * @param hasDependency Set this value as true if backend returns empty secretBase and we know the secret value has been well encrypted in backend
 * If user updates only description only and publish, we'll set secret value as a secureting with Constants.maskedCredential,
 * then backend detects this value is Constants.maskedCredential then knows nothing related to credential is changed, it won't update encryptedCredential.
 */
export function secretBaseToPasswordOrAKV(secretBase: Model.SecretBase, hasDependency: boolean, context: AdapterContext): AzureKeyVault | string | undefined {
    if (secretBase && secretBase.type === 'AzureKeyVaultSecret') {
        return akvToModel(secretBase, context);
    } else {
        return hasDependency || secretBase ? Constants.maskedCredential : undefined;
    }
}

export function akvToResource(secret: AzureKeyVault, context: AdapterContext): Model.AzureKeyVaultSecretReference {
    const result = {
        type: 'AzureKeyVaultSecret',
        store: secret.storageLinkedService ? ReferenceAdapter.toResource(secret.storageLinkedService, context) as Model.LinkedServiceReference : undefined,
        secretName: ValueAdapter.toResource(secret, AzureKeyVault.SECRET_NAME),
        secretVersion: ValueAdapter.toResource(secret, AzureKeyVault.SECRET_VERSION)
    };
    if (secret.linkedServiceParameters && secret.linkedServiceParameters.length > 0) {
        if (result.store) {
            result.store.parameters = fillParameterValuesForResource(secret.linkedServiceParameters, context,
                getCachedServiceInstanceSync<LoggerService>(context.contextData, 'LoggerService'));
        }
    }
    return result;
}
export function akvToModel(secret: Model.SecretBase, context: AdapterContext): AzureKeyVault {
    const akvResource = secret as Model.AzureKeyVaultSecretReference;
    const akv = new AzureKeyVault();
    ValueAdapter.toModel(akv, AzureKeyVault.SECRET_NAME, akvResource.secretName);
    ValueAdapter.toModel(akv, AzureKeyVault.SECRET_VERSION, akvResource.secretVersion);
    if (akvResource.store) {
        akv.storageLinkedService = ReferenceAdapter.toProxyModel<AzureKeyVaultLinkedService>(Metamodel.modelFor(AzureKeyVaultLinkedService),
            akvResource.store, context);
        if (akvResource.store.parameters) {
            akv.linkedServiceParameters = [];
            convertParametersToModel(akv, true, akv.storageLinkedService, akv.linkedServiceParameters, akvResource.store.parameters);
        }
    }
    return akv;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAKVObject(value: any): boolean {
    if (value && typeof value === 'object' && value.type === 'AzureKeyVaultSecret') {
        return true;
    }
    return false;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function referAKVinNonCredentialProperties(obj: any, spec: any, context: AdapterContext): Promise<boolean> {
    // TODO: handle '?'
    if (spec === '*' && isAKVObject(obj)) {
        return true;
    }
    if (typeof obj === 'object') {
        for (const property in obj) {
            const value = obj[property];
            const specValue = (typeof spec === 'object') ? spec[property] : spec;
            if (typeof specValue === 'function') {
                const action = await specValue(context, value);
                if (await referAKVinNonCredentialProperties(value, action, context)) {
                    return true;
                }
            } else {
                // if it's a remainder attribute, it should be in the advanced tab already
                // if it's a secretBase property, UI can handle it
                if (specValue === undefined || specValue === 'secret') {
                    continue;
                } else {
                    if (await referAKVinNonCredentialProperties(value, specValue, context)) {
                        return true;
                    }
                }
            }
        }
    } else if (Array.isArray(obj)) {
        for (const item of obj) {
            if (await referAKVinNonCredentialProperties(item, spec, context)) {
                return true;
            }
        }
    }
    return false;
}

export function parameterizableTextOrAKVtoValue(linkedService: LinkedService, attribute: IAttribute, context: AdapterContext): string | Model.AzureKeyVaultSecretReference {
    // for properties whose type is string | AzureKeyVault, if it's saved as AKV, abandon the parameterized attribute
    if (attribute.value(linkedService) instanceof AzureKeyVault) {
        return akvToResource(attribute.value(linkedService), context);
    } else {
        const modelValue = ValueAdapter.toResource(linkedService, attribute) as string | AzureKeyVault | Model.Expression;
        // For linked service, the payload can't be { 'type': 'Expression', 'value': '@{linkedService().a}' }, it can only be '@{linkedService().a}'
        // no need to call `valueToResource()` of `LinkedServiceAdapter` since it only differs from `ValueAdapter.toResource` when value is not undefined,
        // which is handled by the 1st expression of the ternary expression below.
        return (modelValue && (modelValue as Model.Expression).type === 'Expression') ? (modelValue as Model.Expression).value : attribute.value(linkedService);
    }
}

export function parameterizableValueToModel(linkedService: LinkedService, attribute: IAttribute, resourceValue: string | Model.SecretBase, context: AdapterContext): void {
    if (typeof resourceValue === 'string' || resourceValue === undefined) {
        // For linked service, if a property can be secret base, and it's actually a string, the things stored in the property should be expression
        ValueAdapter.toModel(linkedService, attribute, resourceValue);
    } else {
        attribute.setValue(linkedService, akvToModel(resourceValue, context));
    }
}

export function parameterizablePasswordOrAKVtoSecretBase(linkedService: LinkedService, attribute: IAttribute, context: AdapterContext): string | Model.SecretBase {
    // for properties whose type is string | AzureKeyVault, if it's saved as AKV, abandon the parameterized attribute
    if (attribute.value(linkedService) instanceof AzureKeyVault) {
        return akvToResource(attribute.value(linkedService), context);
    } else {
        // It seems that the type of modelValue must not be AzureKeyVault
        const modelValue = ValueAdapter.toResource(linkedService, attribute) as string | AzureKeyVault | Model.Expression;
        // For linked service, the payload can't be { 'type': 'Expression', 'value': '@{linkedService().a}' }, it can only be '@{linkedService().a}'
        return (modelValue && (modelValue as Model.Expression).type === 'Expression') ? (modelValue as Model.Expression).value :
            passwordOrAKVtoSecretBase(modelValue as string | AzureKeyVault, context);
    }
}

export function parameterizableSecretBaseToModel(linkedService: LinkedService, attribute: IAttribute, resourceValue: string | Model.SecretBase, context: AdapterContext, hasDependency: boolean = true): void {
    if (typeof resourceValue === 'string') {
        // For linked service, if a property can be secret base, and it's actually a string, the things stored in the property should be expression
        attribute.setTemplateValue(linkedService, resourceValue);
    } else {
        attribute.setValue(linkedService, secretBaseToPasswordOrAKV(resourceValue, hasDependency, context));
    }
}

export function checkTestConnectionErrorWithAKV(message: string, linkedService: LinkedService): boolean {
    const secretRegex = /(.*)secretName: (.*), secretVersion: (.*), vaultBaseUrl: https:\/\/(.*)\/\. (.*)/;
    const result = message.match(secretRegex);
    if (!result || result.length < 6) {
        return false;
    }
    const secretName = result[2];
    const secretVersion = result[3];
    const vaultBaseUrl = 'https://' + result[4] + '/';
    for (const property in linkedService) {
        const value = linkedService[property as keyof LinkedService];
        if (value instanceof AzureKeyVault && value.storageLinkedService && value.storageLinkedService.baseUrl
            && value.secretName === secretName && ((!value.secretVersion && !secretVersion) || value.secretVersion === secretVersion)) {
            return vaultBaseUrl !== value.storageLinkedService.baseUrl;
        }
    }
    return false;
}
